import React, { useState, useEffect } from 'react'
import {
  Search,
  Filter,
  Trash2,
  Ban,
  Calendar,
  MapPin,
  Mail,
  Building2,
  AlertCircle,
  RefreshCw,
  ShieldOff,
  UserX
} from 'lucide-react'
import type { NdaAcceptance } from '../lib/storage'

interface NdaAcceptanceManagerProps {
  tenantId: string
}

const NdaAcceptanceManager: React.FC<NdaAcceptanceManagerProps> = ({ tenantId }) => {
  const [acceptances, setAcceptances] = useState<NdaAcceptance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')

  const fetchAcceptances = async () => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams()
      if (searchQuery) params.append('search', searchQuery)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (sourceFilter !== 'all') params.append('source', sourceFilter)

      const response = await fetch(`/api/nda/acceptances/manage?tenantId=${tenantId}&${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch acceptances')
      }

      const data = await response.json()
      setAcceptances(data.acceptances || [])
    } catch (err) {
      console.error('Failed to fetch NDA acceptances:', err)
      setError('Failed to load acceptances')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAcceptances()
  }, [tenantId, statusFilter, sourceFilter])

  const handleSearch = () => {
    fetchAcceptances()
  }

  const handleRevokeAcceptance = async (acceptanceId: string) => {
    if (!confirm('Are you sure you want to revoke this NDA acceptance? The user will be prompted to accept again on their next login.')) {
      return
    }

    try {
      const response = await fetch('/api/nda/acceptances/manage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'revoke',
          acceptanceId
        })
      })

      if (!response.ok) {
        throw new Error('Failed to revoke acceptance')
      }

      await fetchAcceptances()
      setError(null)
    } catch (err) {
      setError('Failed to revoke acceptance')
    }
  }

  const handleDeleteAcceptance = async (acceptanceId: string) => {
    if (!confirm('Are you sure you want to permanently delete this NDA acceptance record? This action cannot be undone.')) {
      return
    }

    try {
      const response = await fetch('/api/nda/acceptances/manage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'delete',
          acceptanceId
        })
      })

      if (!response.ok) {
        throw new Error('Failed to delete acceptance')
      }

      await fetchAcceptances()
      setError(null)
    } catch (err) {
      setError('Failed to delete acceptance')
    }
  }

  const handleBypassUser = async (userEmail: string) => {
    if (!confirm(`Bypass NDA requirement for ${userEmail}? They will not be prompted for NDA acceptance.`)) {
      return
    }

    try {
      const response = await fetch('/api/nda/bypasses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'add',
          company: userEmail // Can be email or company
        })
      })

      if (!response.ok) {
        throw new Error('Failed to add bypass')
      }

      await fetchAcceptances()
      setError(null)
    } catch (err) {
      setError('Failed to add bypass')
    }
  }

  const handleBypassCompany = async (company: string) => {
    if (!confirm(`Bypass NDA requirement for all users from ${company}? No users from this company will be prompted for NDA acceptance.`)) {
      return
    }

    try {
      const response = await fetch('/api/nda/bypasses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'add',
          company: company
        })
      })

      if (!response.ok) {
        throw new Error('Failed to add bypass')
      }

      await fetchAcceptances()
      setError(null)
    } catch (err) {
      setError('Failed to add bypass')
    }
  }

  const getStatusBadge = (acceptance: NdaAcceptance) => {
    if (acceptance.status === 'active') {
      // Check if expired
      if (acceptance.expiresAt && new Date() > new Date(acceptance.expiresAt)) {
        return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Expired</span>
      }
      return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Active</span>
    }
    if (acceptance.status === 'revoked') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">Revoked</span>
    }
    if (acceptance.status === 'expired') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Expired</span>
    }
    return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">{acceptance.status}</span>
  }

  const getSourceBadge = (source: string) => {
    const colors = {
      clickwrap: 'bg-blue-100 text-blue-700',
      crm: 'bg-purple-100 text-purple-700',
      manual: 'bg-gray-100 text-gray-700'
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[source as keyof typeof colors] || 'bg-gray-100 text-gray-700'}`}>
        {source}
      </span>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          View and manage all NDA acceptances for your Trust Center. You can search, filter, revoke, or delete acceptance records.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by email, company, location..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Search
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>

          {/* Source Filter */}
          <div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Sources</option>
              <option value="clickwrap">Clickwrap</option>
              <option value="crm">CRM</option>
              <option value="manual">Manual</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {acceptances.length} acceptance{acceptances.length !== 1 ? 's' : ''} found
          </p>
          <button
            onClick={fetchAcceptances}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Acceptances Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : acceptances.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <p className="text-gray-500">No acceptances found matching your criteria.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Company
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Accepted
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Source
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Location
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {acceptances.map((acceptance) => (
                  <tr key={acceptance.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 break-all">
                          {acceptance.userEmail || 'Unknown'}
                        </span>
                      </div>
                      <div className="sm:hidden text-xs text-gray-500 mt-1">
                        {acceptance.userCompany || acceptance.userEmail?.split('@')[1] || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">
                          {acceptance.userCompany || acceptance.userEmail?.split('@')[1] || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {new Date(acceptance.acceptedAt).toLocaleDateString()}
                        </span>
                      </div>
                      {acceptance.expiresAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Exp: {new Date(acceptance.expiresAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                      {getSourceBadge(acceptance.ndaSource)}
                      {acceptance.clickwrapVersion && (
                        <div className="text-xs text-gray-500 mt-1">
                          v{acceptance.clickwrapVersion}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      {getStatusBadge(acceptance)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden lg:table-cell">
                      {acceptance.geolocation?.city && acceptance.geolocation?.country ? (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700">
                            {acceptance.geolocation.city}, {acceptance.geolocation.country}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                      {acceptance.ipAddress && (
                        <div className="text-xs text-gray-500 mt-1">
                          IP: {acceptance.ipAddress}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <button
                          onClick={() => handleRevokeAcceptance(acceptance.id)}
                          className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors touch-manipulation"
                          title="Revoke acceptance (user will be prompted again)"
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleBypassUser(acceptance.userEmail || '')}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors touch-manipulation"
                          title="Bypass NDA for this user"
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleBypassCompany(acceptance.userCompany || acceptance.userEmail?.split('@')[1] || '')}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors touch-manipulation"
                          title="Bypass NDA for this company"
                        >
                          <ShieldOff className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAcceptance(acceptance.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors touch-manipulation"
                          title="Delete acceptance record"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default NdaAcceptanceManager
